import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, User, MessageSquare, Shield, Scale, FileSearch, Building2, Users, Briefcase } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const SoloPersonalInjuryLawyerPage = () => {
  const faqData = [
    {
      question: "What are the benefits of working with a solo personal injury lawyer?",
      answer: "Working with a solo attorney means you work directly with the lawyer handling your case throughout the entire process. You won't be handed off to paralegals or case managers for important decisions. This allows for clearer communication and more personalized attention to your situation."
    },
    {
      question: "Will I work directly with Amy Brown on my case?",
      answer: "Yes. When you hire Amy C. Brown Law, you work directly with Amy throughout your case. She handles the strategy, communication, and decision-making personally rather than delegating to staff members."
    },
    {
      question: "How is a solo practice different from a large personal injury firm?",
      answer: "Large firms often handle high volumes of cases and may rely heavily on staff and case managers. A solo practice can provide more individualized attention, direct attorney communication, and a thoughtful approach tailored to your specific situation."
    },
    {
      question: "Does a solo attorney have the resources to handle my case?",
      answer: "Yes. Solo attorneys regularly handle personal injury cases of all sizes. Amy has experience with car accidents, slip and falls, maritime injuries, and other personal injury matters throughout Washington State."
    }
  ];

  const serviceData = {
    name: "Solo Personal Injury Lawyer",
    description: "Personal injury representation with direct attorney access, clear communication, and individualized attention from a solo practitioner.",
    category: "Personal Injury Law"
  };

  return (
    <div className="min-h-screen pt-20" data-testid="solo-lawyer-page">
      <SEOHead
        title="Solo Personal Injury Lawyer in Everett | Amy C. Brown Law"
        description="Work directly with your attorney throughout your injury case. Amy C. Brown Law offers personal attention, clear communication, and a thoughtful approach to personal injury claims in Everett and Snohomish County."
        keywords="solo personal injury lawyer, small law firm, direct attorney access, personal injury attorney Everett"
        canonicalUrl="https://amycbrownlaw.com/solo-personal-injury-lawyer"
        faqData={faqData}
        serviceData={serviceData}
        localArea={{ city: "Everett", county: "Snohomish", state: "WA" }}
      />

      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <p className="text-primary font-medium tracking-wide uppercase text-sm mb-4">Personalized Legal Representation</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-8 leading-tight max-w-4xl">
            Working With a Solo Personal Injury Lawyer
          </h1>
          <p className="text-xl md:text-2xl text-slate-600 leading-relaxed max-w-2xl">
            Direct access to your attorney. Clear communication. A thoughtful approach to your case.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <p className="text-lg md:text-xl text-slate-700 leading-relaxed mb-8">
            Some clients intentionally seek out a smaller law firm or solo attorney rather than a large personal injury practice. Many people tell me they value working directly with the lawyer who is actually handling their case.
          </p>
          <p className="text-lg md:text-xl text-slate-700 leading-relaxed">
            Injury cases can feel overwhelming. Between medical appointments, insurance calls, and uncertainty about what comes next, the last thing most people want is to feel like just another file number at a busy law firm. Some clients prefer a more personal and thoughtful approach—working with an attorney who knows their name, understands their situation, and is available to answer questions directly.
          </p>
        </div>
      </section>

      {/* Direct Access to Your Attorney */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <User className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Direct Access to Your Attorney
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-8">
            When you work with me, you work directly with the attorney handling your case—from the first conversation through resolution. This direct relationship allows you to:
          </p>
          
          <div className="grid md:grid-cols-3 gap-5 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-secondary mb-2">Ask Questions Directly</h3>
              <p className="text-slate-600 text-sm">Get answers from someone who knows your case, not a receptionist or case manager</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <FileSearch className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-secondary mb-2">Receive Clear Explanations</h3>
              <p className="text-slate-600 text-sm">Understand what's happening with your case and why, in plain English</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-secondary mb-2">Stay Informed</h3>
              <p className="text-slate-600 text-sm">Know the progress of your case without chasing down updates</p>
            </div>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            Clear communication is a priority in my practice. Whether you've been injured in a <Link to="/car-crashes" className="text-primary hover:underline">car accident</Link>, a <Link to="/slip-and-falls" className="text-primary hover:underline">slip and fall</Link>, or another incident, you deserve to understand what's happening with your case.
          </p>
        </div>
      </section>

      {/* A Thoughtful Approach to Case Strategy */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <Scale className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              A Thoughtful Approach to Case Strategy
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Injury cases deserve careful attention, not a rush to settlement. My approach focuses on building your case thoughtfully rather than pushing for a quick resolution that may not reflect your full damages.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            This means taking time to:
          </p>
          
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 rounded-lg p-5 border-l-4 border-primary">
              <h3 className="font-semibold text-secondary mb-2">Understand the Details</h3>
              <p className="text-slate-600">Learning exactly what happened in your accident and how it has affected your life</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-5 border-l-4 border-primary">
              <h3 className="font-semibold text-secondary mb-2">Gather Documentation</h3>
              <p className="text-slate-600">Collecting medical records, bills, and evidence that supports your claim</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-5 border-l-4 border-primary">
              <h3 className="font-semibold text-secondary mb-2">Evaluate Coverage</h3>
              <p className="text-slate-600">Identifying all available insurance coverage that may apply to your situation</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-5 border-l-4 border-primary">
              <h3 className="font-semibold text-secondary mb-2">Build a Strong Case</h3>
              <p className="text-slate-600">Presenting your claim in a way that insurance companies take seriously</p>
            </div>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            I serve clients throughout Washington State, with a focus on <Link to="/everett-personal-injury-lawyer" className="text-primary hover:underline">Everett and Snohomish County</Link>. Whether your case is straightforward or complex, you'll receive the same level of attention and care.
          </p>
        </div>
      </section>

      {/* Avoiding the Settlement Factory Model */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <Building2 className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              A Different Approach Than High-Volume Firms
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Some personal injury firms handle a high volume of cases and rely heavily on staff members and case managers to communicate with clients. That model works for some people, but it's not the right fit for everyone.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            A smaller practice allows me to provide a more individualized approach to each client's situation. Rather than processing cases quickly, I focus on understanding what happened to you and building the strongest possible claim on your behalf.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            This doesn't mean larger firms are bad—many do excellent work. It simply means that if you value direct communication with your attorney and a more personal approach, working with a solo practitioner may be a good fit for you.
          </p>
        </div>
      </section>

      {/* Understanding the Insurance Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Understanding the Insurance Process
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Earlier in my career, I worked on legal matters involving insurance claims. That experience gave me insight into how insurance companies evaluate cases, what factors they consider when making settlement decisions, and the strategies they use during the claims process.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            Understanding the insurance process from the inside helps me build stronger cases for injured clients. I know what documentation matters, how to present damages effectively, and when a settlement offer doesn't reflect the true value of a claim.
          </p>
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <Briefcase className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Personal Attention for Your Injury Claim
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Every client deserves clear guidance and personal attention when dealing with an injury claim. You shouldn't have to wonder who's handling your case or wait days for a return phone call.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            If you're looking for an attorney who will work directly with you, explain things clearly, and approach your case thoughtfully, I welcome the opportunity to speak with you about your situation. You can <Link to="/contact" className="text-primary hover:underline">contact the office</Link> to schedule a conversation—there's no pressure and no obligation.
          </p>
        </div>
      </section>

      {/* Soft CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Questions About Your Situation?
          </h2>
          <p className="text-slate-200 mb-8 text-lg">
            If you have questions about your situation or would like to discuss your case, you're welcome to contact the office to schedule a conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+14256751224"
              className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-full font-medium transition-all"
              data-testid="solo-lawyer-call-btn"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a
              href="sms:+14256751224"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 px-8 py-4 rounded-full font-medium transition-all"
              data-testid="solo-lawyer-text-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>

      {/* Wikipedia Entity Links */}
      <section className="py-8 bg-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <p className="text-sm text-slate-500 text-center">
            Related legal concepts:{' '}
            <a href="https://en.wikipedia.org/wiki/Personal_injury" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Personal Injury</a>
            {' · '}
            <a href="https://en.wikipedia.org/wiki/Attorney%E2%80%93client_privilege" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Attorney-Client Privilege</a>
            {' · '}
            <a href="https://en.wikipedia.org/wiki/Contingent_fee" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Contingency Fee</a>
            {' · '}
            <a href="https://en.wikipedia.org/wiki/Solo_practice" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Solo Practice</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default SoloPersonalInjuryLawyerPage;
